// /src/pages/private/admin/academyTabs/courseExam/CourseExamQuestion.jsx

import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cellKey } from "../../../../../../utils/courseExamScoring";

const QUESTION_TYPE_LABELS = {
  single: "Single select",
  multiple: "Multiple select",
  grid: "Grid (Matrix)",
  truefalse: "True / False",
  ranking: "Ranking",
};

function questionTypeLabel(type) {
  return QUESTION_TYPE_LABELS[String(type || "")] || String(type || "");
}

function toggleOptionId(list, optionId, single = false) {
  if (single) return [optionId];
  const set = new Set(list || []);
  if (set.has(optionId)) set.delete(optionId);
  else set.add(optionId);
  return [...set];
}

function SortableRankingRow({ id, rankIndex, label }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="exam-ranking-row exam-ranking-row--sortable"
    >
      <button
        type="button"
        className="exam-ranking-row__handle"
        aria-label={`Reorder item ${rankIndex + 1}`}
        {...attributes}
        {...listeners}
      >
        ≡
      </button>
      <span className="exam-ranking-row__index">{rankIndex + 1}</span>
      <span className="exam-ranking-row__label">{label}</span>
    </div>
  );
}

function ExamRankingList({ rankingOrder, optionById, onChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = rankingOrder.indexOf(active.id);
        const newIndex = rankingOrder.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onChange(arrayMove(rankingOrder, oldIndex, newIndex));
      }}
    >
      <SortableContext items={rankingOrder} strategy={verticalListSortingStrategy}>
        <div className="exam-ranking-list">
          {rankingOrder.map((optionId, rankIndex) => {
            const option = optionById[optionId];
            if (!option) return null;

            return (
              <SortableRankingRow
                key={optionId}
                id={optionId}
                rankIndex={rankIndex}
                label={option.label}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function CourseExamQuestion({
  question,
  answer,
  onChange,
  index,
}) {
  const type = String(question.type || "");
  const selectedIds = answer?.selectedOptionIds || [];
  const rankingOrder = answer?.rankingOrder || [];
  const gridSelections = answer?.gridSelections || [];
  const selectedGridKeys = new Set(
    gridSelections.map((cell) => cellKey(cell.row, cell.column))
  );

  const optionById = Object.fromEntries(
    (question.options || []).map((option) => [option.id, option])
  );

  const toggleGridCell = (row, column) => {
    const key = cellKey(row, column);
    const isSingle = type === "grid" && (question.subType || "single") === "single";

    if (isSingle) {
      const withoutRow = gridSelections.filter((cell) => cell.row !== row);
      onChange({
        ...answer,
        gridSelections: [...withoutRow, { row, column }],
      });
      return;
    }

    if (selectedGridKeys.has(key)) {
      onChange({
        ...answer,
        gridSelections: gridSelections.filter(
          (cell) => cellKey(cell.row, cell.column) !== key
        ),
      });
      return;
    }

    onChange({
      ...answer,
      gridSelections: [...gridSelections, { row, column }],
    });
  };

  return (
    <div className="exam-question exam-question--playable">
      <div className="exam-question__head">
        <span className="exam-question__index">Q{index + 1}</span>
        {question.mandatory && (
          <span className="exam-question__badge">Mandatory</span>
        )}
        {question.difficulty && (
          <span className="exam-question__badge exam-question__badge--muted">
            {question.difficulty}
          </span>
        )}
        {question.topic && (
          <span className="exam-question__badge exam-question__badge--topic">
            {question.topic}
          </span>
        )}
        {type && (
          <span className="exam-question__badge exam-question__badge--type">
            {questionTypeLabel(type)}
          </span>
        )}
      </div>

      <div className="exam-title">{question.title}</div>

      {type === "truefalse" && (
        <div className="exam-truefalse-list">
          {(question.options || []).map((option) => {
            const checked = selectedIds.includes(option.id);

            return (
              <div key={option.id} className="exam-truefalse-row">
                <span className="exam-truefalse-row__label">{option.label}</span>
                <input
                  type="radio"
                  className="exam-truefalse-row__radio"
                  name={`exam-q-${question.questionId}`}
                  checked={checked}
                  onChange={() =>
                    onChange({
                      ...answer,
                      selectedOptionIds: [option.id],
                    })
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      {(type === "single" || type === "multiple") &&
        (question.options || []).map((option) => {
          const inputType = type === "multiple" ? "checkbox" : "radio";
          const checked = selectedIds.includes(option.id);

          return (
            <label key={option.id} className="exam-option exam-option--playable">
              <input
                type={inputType}
                name={`exam-q-${question.questionId}`}
                checked={checked}
                onChange={() =>
                  onChange({
                    ...answer,
                    selectedOptionIds: toggleOptionId(
                      selectedIds,
                      option.id,
                      inputType === "radio"
                    ),
                  })
                }
              />
              <span>{option.label}</span>
            </label>
          );
        })}

      {type === "ranking" && (
        <ExamRankingList
          rankingOrder={rankingOrder}
          optionById={optionById}
          onChange={(nextOrder) =>
            onChange({
              ...answer,
              rankingOrder: nextOrder,
            })
          }
        />
      )}

      {type === "grid" && (
        <div className="exam-grid-list">
          {(question.columns || []).length > 0 && (
            <div className="exam-grid-list__head" aria-hidden="true">
              <span className="exam-grid-list__head-spacer" />
              <div className="exam-grid-list__head-cols">
                {(question.columns || []).map((column, columnIndex) => (
                  <span key={columnIndex} className="exam-grid-list__col-label">
                    {column}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(question.rows || []).map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={`exam-grid-row${rowIndex % 2 === 1 ? " exam-grid-row--alt" : ""}`}
            >
              <span className="exam-grid-row__label">{row}</span>
              <div className="exam-grid-row__choices">
                {(question.columns || []).map((column, columnIndex) => {
                  const key = cellKey(row, column);
                  const checked = selectedGridKeys.has(key);
                  const inputType =
                    (question.subType || "single") === "single" ? "radio" : "checkbox";

                  return (
                    <label key={columnIndex} className="exam-grid-row__choice">
                      <input
                        type={inputType}
                        className="exam-grid-row__input"
                        aria-label={`${row} — ${column}`}
                        name={
                          inputType === "radio"
                            ? `exam-grid-${question.questionId}-${row}`
                            : undefined
                        }
                        checked={checked}
                        onChange={() => toggleGridCell(row, column)}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
